import React, { useState, useRef, useEffect } from 'react';
import { useSupport } from '../../redux/ducks/support';
import { isListNotEmpty, isStringNotEmpty } from '../../utils/objectUtils';
import SupportChatMessage from './SupportChatMessage';

/**
 * SupportChatPanel - The main chat interface
 *
 * Displays conversation messages with real-time updates via Firestore listener.
 * Shows typing indicator while AI processes, and handles escalation/redirect actions.
 */
const SupportChatPanel = ({ conversationId }) => {

	//#region useSupport

	const {
		messages,
		isAiTyping,
		sendSupportMessage,
		hasDoctorRedirect,
		hasEscalation,
		hasTicketCreated,
		error
	} = useSupport();

	//#endregion
	//#region useStates

	const [inputValue, setInputValue] = useState('');
	const [isSending, setIsSending] = useState(false);
	const messagesEndRef = useRef(null);
	const inputRef = useRef(null);

	//#endregion
	//#region autoScrollAndFocus

	// Auto-scroll to bottom when new messages arrive or AI starts typing
	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [messages, isAiTyping]);

	// Focus the input field on mount so the user can start typing immediately
	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, []);

	//#endregion
	//#region handleSendAndKeyDown

	/**
	 * Sends the current input value as a message, then clears the input
	 * and refocuses it for the next message.
	 */
	const handleSend = async () => {
		const message = inputValue.trim();
		if (!isStringNotEmpty(message) || isSending) return;

		setInputValue('');
		setIsSending(true);

		await sendSupportMessage(conversationId, message);

		setIsSending(false);

		if (inputRef.current) {
			inputRef.current.focus();
		}
	};

	/**
	 * Allows sending a message with Enter key (Shift+Enter for newline)
	 */
	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	//#endregion
	//#region returnJSX

	return (
		<div className="support-chat-panel">

			{/* Messages Area */}
			<div className="support-chat-messages">

				{/* Welcome message - shown when no messages exist yet */}
				{!isListNotEmpty(messages) && !isAiTyping && (
					<div className="support-chat-welcome">
						<div className="support-chat-welcome-avatar">
							<i className="fas fa-headset"></i>
						</div>
						<p className="mb-1 fw-semibold">Hi there!</p>
						<p className="text-muted small mb-0">
							I'm Ava, your ReadyRx support assistant. How can I help you today?
						</p>
					</div>
				)}

				{/* Message list - renders each message as a SupportChatMessage bubble */}
				{isListNotEmpty(messages) && messages.map((msg) => (
					<SupportChatMessage key={msg.id} message={msg} />
				))}

				{/* Typing indicator - animated dots while AI is generating a response */}
				{isAiTyping && (
					<div className="support-chat-typing">
						<div className="support-chat-typing-avatar">
							<i className="fas fa-headset"></i>
						</div>
						<div className="support-chat-typing-dots">
							<span></span>
							<span></span>
							<span></span>
						</div>
					</div>
				)}

				{/* Doctor redirect notice - shown when AI redirects a medical question */}
				{hasDoctorRedirect && (
					<div className="support-chat-notice support-chat-notice-info">
						<i className="fas fa-user-md me-2"></i>
						<span>
							For medical questions, please use our <strong>Doctor Chat</strong> feature where licensed healthcare providers can assist you.
						</span>
					</div>
				)}

				{/* Escalation notice - shown when AI hands off to a human agent */}
				{hasEscalation && (
					<div className="support-chat-notice support-chat-notice-warning">
						<i className="fas fa-user-tie me-2"></i>
						<span>
							A support specialist has been notified and will join this conversation shortly.
						</span>
					</div>
				)}

				{/* Ticket created notice - shown when AI creates a tracking ticket */}
				{hasTicketCreated && (
					<div className="support-chat-notice support-chat-notice-success">
						<i className="fas fa-ticket-alt me-2"></i>
						<span>
							Ticket <strong>{hasTicketCreated.ticketNumber}</strong> has been created to track your issue.
						</span>
					</div>
				)}

				{/* Error message - shown when a send or AI call fails */}
				{isStringNotEmpty(error) && (
					<div className="support-chat-notice support-chat-notice-danger">
						<i className="fas fa-exclamation-circle me-2"></i>
						<span>{error}</span>
					</div>
				)}

				{/* Invisible anchor element for auto-scroll targeting */}
				<div ref={messagesEndRef} />
			</div>

			{/* Input Area - textarea with send button */}
			<div className="support-chat-input-area">
				<div className="support-chat-input-container">
					<textarea
						ref={inputRef}
						className="support-chat-input"
						placeholder="Type your message..."
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						onKeyDown={handleKeyDown}
						disabled={isSending || isAiTyping}
						rows={1}
					/>
					<button
						className="support-chat-send-btn"
						onClick={handleSend}
						disabled={!isStringNotEmpty(inputValue.trim()) || isSending || isAiTyping}
						aria-label="Send message"
					>
						<i className="fas fa-paper-plane"></i>
					</button>
				</div>
			</div>
		</div>
	);
	//#endregion
};

export default SupportChatPanel;
